import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useVideoControls } from '../hooks/use-video-controls';
const playIcon = require('../../../../../../../../../assets/images/marketplace/play.svg');

interface CustomVideoPlayerProps {
  videoUrl: string;
  thumbnailUrl?: string;
  onPlay?: () => void;
  onPause?: () => void;
}

export interface CustomVideoPlayerRef {
  pause: () => void;
  isPlaying: boolean;
  isPaused: boolean;
  hasError: boolean;
}

export const CustomVideoPlayer = forwardRef<CustomVideoPlayerRef, CustomVideoPlayerProps>(
  ({ videoUrl, thumbnailUrl, onPlay, onPause }, ref) => {
    const { t } = useTranslation();
    const { videoRef, isPlaying, isPaused, hasError, handlePlay, handlePause, handleEnded, handleError, pause } =
      useVideoControls(onPlay, onPause);

    useImperativeHandle(ref, () => ({
      pause,
      isPlaying,
      isPaused,
      hasError
    }));

    return (
      <div className="relative h-full w-full">
        <video
          ref={videoRef}
          src={videoUrl}
          poster={thumbnailUrl}
          className="h-full w-full object-cover rounded-[24px]"
          playsInline
          onClick={isPlaying ? handlePause : undefined}
          onEnded={handleEnded}
          onError={handleError}
          preload="none"
        />

        {/* Error message */}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-[24px]">
            <p className="text-white text-sm px-4 text-center">{t('js.features.marketplace.home.what_our_housemates_say_section.unable_to_load_video')}</p>
          </div>
        )}

        {/* Custom play button - centered on mobile, bottom-right on sm+ */}
        {!isPlaying && !hasError && (
          <button
            onClick={handlePlay}
            className={clsx(
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10',
              'sm:top-auto sm:left-auto sm:bottom-4 sm:right-4 sm:translate-x-0 sm:translate-y-0',
              'flex h-[56px] w-[56px] items-center justify-center rounded-full',
              'bg-bg-secondaryHeavy/30 backdrop-blur-sm shadow-lg',
              'transition-all hover:scale-110 hover:bg-white'
            )}
            aria-label="Play video"
          >
            <img src={playIcon} alt="" className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  }
);

CustomVideoPlayer.displayName = 'CustomVideoPlayer';
